import { createContext, useContext, useCallback, useMemo, useEffect, type ReactNode } from 'react'
import { useChatStore } from '../stores/chatStore'

/**
 * ChatContext - Scaled Down Version (Phase 4.1)
 * 
 * Only provides stable identifiers and generic utilities.
 * High-frequency data (messages, streaming) is consumed via atomic Zustand selectors.
 */

interface ChatContextType {
    // ===== IDENTIFICATION STABLE =====
    currentAgentId: string | null
    currentAgentName: string

    // ===== GENERIC ACTIONS =====
    copyMessage: (content: string, index?: number) => Promise<void>
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

export const useChatContext = () => {
    const context = useContext(ChatContext)
    if (!context) {
        throw new Error('useChatContext must be used within ChatProvider')
    }
    return context
}

interface ChatProviderProps {
    children: ReactNode
    agentId: string | null
    agentName: string
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ agentId, agentName, children }) => {
    const initAgent = useChatStore(state => state.initAgent)

    // Ensure agent is initialized in store
    useEffect(() => {
        if (agentId && agentName) {
            initAgent(agentId, agentName)
        }
    }, [agentId, agentName, initAgent])

    const copyMessage = useCallback(async (content: string, index?: number) => {
        try {
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = content
            const plainText = tempDiv.textContent || tempDiv.innerText || ''
            await navigator.clipboard.writeText(plainText)
            if (index !== undefined) {
                console.log(`Message ${index} copied`)
            }
        } catch (err) {
            console.error('Failed to copy', err)
        }
    }, [])

    const value = useMemo(() => ({
        currentAgentId: agentId,
        currentAgentName: agentName,
        copyMessage
    }), [agentId, agentName, copyMessage])

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    )
}
